import React, { useMemo } from "react";
import styled from "styled-components";
import {
    ActivateFeatureButton,
    Button,
    DataTable,
    DataTableEditActionButton,
    DataTableCell,
    DataTableContainer,
    DataTableHeaderCell,
    FlexContainer,
    LottieAnimation,
    PortalSearchInput,
    PortalSelect,
    PortalTableActions,
    PortalTableControls,
    UpperControls
} from "src/components";
import FilterIcon from "src/img/filter.svg";
import moment from "moment";
import Checkbox from "src/scenes/EmployeeList/components/Checkbox";
import PurpleLottieJson from "src/scenes/Noticeboard/assets/purple-lottie.json";
import pluralize from "pluralize";
import { capitalizeString, customNames } from "src/utils/helpers";

const UpperButtons = styled(Button)`
    border-radius: 8px !important;
    border: ${p => p.border || "none"};
    height: 42px;
    color: ${p => p.color || "var(--calendar-action-primary-text, #fff)"};
    background-color: ${p => p.backgroundColor || "var(--calendar-action-primary-bg, #5B6EF5)"};
    box-sizing: border-box;
    font-size: 0.875rem;
    font-weight: 700;
    padding: ${p => p.padding};
    display: flex;
    align-items: center;
    justify-content: center;
`;

const ContactImgContainer = styled.div`
    width: 70px;
    height: 40px;
    display: flex;
    justify-content: center;
    border: 1px solid var(--calendar-border, #D8DEEC);
    border-radius: 8px;
`;

const ContactImg = styled.img`
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 8px;
    align-self: center;
`;

const TdText = styled.p`
    font-weight: 500;
    font-size: 0.875rem;
    white-space: normal;
    margin: 0;
`;

export const FILTER_BY = {
    ALL_CONTACTS: "all",
    EXPIRED: "expired",
    NO_EXPIRY: "no-expiry"
};

export const FILTER_DIRECTORY = {
    EMPLOYEE: "employee",
    CLIENT: "client",
    EMPLOYEE_AND_CLIENT: "employee_and_client"
};

const DirectoryTable = ({
    contactsArray,
    showAddContactForm,
    showEditContactForm,
    showDeleteDialog,
    checkedContacts,
    setCheckedContacts,
    isFetching,
    filterOption,
    filterByExpiry,
    changeTableFilter,
    sortOption,
    setSearchQuery,
    searchQuery,
    handleShowSortModal
}) => {
    const filteredAndSortedContacts = useMemo(() => {
        const now = new Date();
  
        const filtered = contactsArray.filter(contact => {
            const rawDate = contact.expirationDate;
            const expirationDate =
                rawDate && rawDate !== "N/A" && rawDate !== "" && rawDate !== "0000-00-00"
                    ? new Date(rawDate)
                    : null;

            const isClient =
                contact.visibleToClients === true ||
                contact.visibleToClients === 1 ||
                contact.visibleToClients === "1";

            const isEmployee =
                contact.visibleToEmployees === true ||
                contact.visibleToEmployees === 1 ||
                contact.visibleToEmployees === "1";

            // Directory filter
            if (
                (filterOption === "employee" && !isEmployee) ||
                (filterOption === "client" && !isClient) ||
                (filterOption === "employee_and_client" && !isEmployee && !isClient)
            ) {
                return false;
            }

            // Expiry filter
            if (filterByExpiry === "expired") {
                if (!(expirationDate instanceof Date) || isNaN(expirationDate) || expirationDate >= now) {
                    return false;
                }
            } else if (filterByExpiry === "no-expiry") {
                if (expirationDate) {
                    return false;
                }
            }

            return true;
        });

        // Search filtering here
        const searched = filtered.filter(contact => {
            if (!searchQuery?.trim()) return true;
            const lowerQuery = searchQuery.toLowerCase();
            return (
                contact.title?.toLowerCase().includes(lowerQuery) ||
                contact.position?.toLowerCase().includes(lowerQuery) ||
                contact.department?.toLowerCase().includes(lowerQuery) ||
                contact.email?.toLowerCase().includes(lowerQuery) ||
                contact.phoneNumber?.toLowerCase().includes(lowerQuery) ||
                contact.country?.toLowerCase().includes(lowerQuery)
            );
        });

        const sorted = [...searched].sort((a, b) => {
            const getValue = (contact) => {
                switch (sortOption) {
                    case "name":
                        return (contact.name || "").toLowerCase();
                    case "company":
                        return (contact.company || "").toLowerCase();
                    case "position":
                        return (contact.position || "").toLowerCase();
                    case "department":
                        return (contact.department || "").toLowerCase();
                    case "location":
                        return (contact.country || "").toLowerCase();
                    default:
                        return "";
                }
            };

            return getValue(a).localeCompare(getValue(b));
        });

        return sorted;
    }, [contactsArray, filterOption, filterByExpiry, sortOption, searchQuery]);

    const checkAllContacts = useMemo(() => {
        return filteredAndSortedContacts.length > 0 && checkedContacts.length === filteredAndSortedContacts.length;
    }, [checkedContacts]);

    const handleCheckAllContacts = () => {
        if (checkAllContacts) {
            setCheckedContacts([]);
        } else {
            setCheckedContacts(filteredAndSortedContacts.map(contact => contact.uuid));
        }
    };

    const handleCheckboxChange = (contactUuid) => {
        const checked = checkedContacts.includes(contactUuid);
        setCheckedContacts(prevChecked => {
            if (checked) {
                return prevChecked.filter(uuid => uuid !== contactUuid);
            } else {
                return [...prevChecked, contactUuid];
            }
        });
    };
    
    const getUserGroup = contact => {
        if (contact.visibleToEmployees && contact.visibleToClients) {
            return `${capitalizeString(pluralize.singular(customNames.employees))} & ${capitalizeString(pluralize.singular(customNames.clients))}`;
        } else if (contact.visibleToEmployees) {
            return `${capitalizeString(pluralize.singular(customNames.employees))}`;
        } else if (contact.visibleToClients) {
            return `${capitalizeString(pluralize.singular(customNames.clients))}`;
        } else {
            return "";
        }
    };

    const trimText = description => {
        if (description && description.length > 117) {
            return description.slice(0, 117).trim() + "...";
        }
        return description ?? "";
    };

    const handleSearchChange = event => {
        setSearchQuery(event.target.value);
    };

    const formatExpiryDate = date => {
        if (date === null) return "None";
        const currentDate = moment();
        const dateToCheck = moment(date);
        if (dateToCheck.isBefore(currentDate)) {
            return (<span style={{ color: "#ff7171", fontWeight: 600 }}>{dateToCheck.format("Do MMMM, YYYY") + " Expired"}</span>);
        }
        return dateToCheck.format("Do MMMM, YYYY");
    };

    const tableHeaderWidths = ["5%", "5%", "8.5%", "8.5%", "8.5%", "8.5%", "8.5%", "10%", "8.5%", "8.5%", "8.5%", "8.5%",];

    const tableHeader = [
        <label>
            <Checkbox
                key="selectAll"
                readOnly={false}
                checked={checkAllContacts}
                onChange={() => handleCheckAllContacts(filteredAndSortedContacts)}
            />
        </label>,
        "Image",
        "Name",
        "Title/Position",
        "Department",
        "Email",
        "Phone",
        "Address",
        "Location",
        "Expiry Date",
        "Visible to",
        "View/Edit Listing"
    ];

    const headings = tableHeader.map((heading, index) => (
        <DataTableHeaderCell key={index} width={tableHeaderWidths[index]} style={{ textAlign: index === 0 || index === 11 ? "center" : "left" }}>
            {heading}
        </DataTableHeaderCell>
    ));

    const contactsList = filteredAndSortedContacts.map(contact => {
        return (
            <tr key={contact.uuid}>
                <DataTableCell textAlign="center">
                    <label>
                        <Checkbox
                            readOnly={false}
                            checked={checkedContacts.includes(contact.uuid)}
                            onChange={() => handleCheckboxChange(contact.uuid)}
                        />
                    </label>
                </DataTableCell>
                <DataTableCell>
                    {contact.imageUrl ?
                        <ContactImgContainer>
                            <ContactImg src={contact.imageUrl} />
                        </ContactImgContainer> :
                        <TdText>{"No Image"}</TdText>}
                </DataTableCell>
                <DataTableCell whiteSpace="normal"><TdText>{contact.personalName ? contact.personalName : contact.company}</TdText></DataTableCell>
                <DataTableCell whiteSpace="normal"><TdText>{trimText(contact.position)}</TdText></DataTableCell>
                <DataTableCell whiteSpace="normal"><TdText>{trimText(contact.department)}</TdText></DataTableCell>
                <DataTableCell whiteSpace="normal"><TdText>{trimText(contact.email)}</TdText></DataTableCell>
                <DataTableCell whiteSpace="normal"><TdText>{contact.phoneNumber}</TdText></DataTableCell>
                <DataTableCell whiteSpace="normal"><TdText>{contact.address}</TdText></DataTableCell>
                <DataTableCell whiteSpace="normal"><TdText>{contact.country}</TdText></DataTableCell>
                <DataTableCell whiteSpace="normal"><TdText>{formatExpiryDate(contact.expirationDate)}</TdText></DataTableCell>
                <DataTableCell whiteSpace="normal"><TdText>{getUserGroup(contact)}</TdText></DataTableCell>
                <DataTableCell textAlign="center">
                    <DataTableEditActionButton
                        onClick={() => showEditContactForm(contact)}
                        minWidth="6.25rem"
                    >
                        View/Edit
                    </DataTableEditActionButton>
                </DataTableCell>
            </tr>
        );
    });

    // Pad with placeholder rows if less than 10 contacts
    const minRows = 10;
    const placeholdersNeeded = minRows - filteredAndSortedContacts.length;

    for (let i = 0; i < placeholdersNeeded; i++) {
        contactsList.push(
            <tr key={`empty_${i}`} className="placeholder-row">
                <DataTableCell $empty>&nbsp;</DataTableCell>
                <DataTableCell $empty>&nbsp;</DataTableCell>
                <DataTableCell $empty>&nbsp;</DataTableCell>
                <DataTableCell $empty>&nbsp;</DataTableCell>
                <DataTableCell $empty>&nbsp;</DataTableCell>
                <DataTableCell $empty>&nbsp;</DataTableCell>
                <DataTableCell $empty>&nbsp;</DataTableCell>
                <DataTableCell $empty>&nbsp;</DataTableCell>
                <DataTableCell $empty>&nbsp;</DataTableCell>
                <DataTableCell $empty>&nbsp;</DataTableCell>
                <DataTableCell $empty>&nbsp;</DataTableCell>
                <DataTableCell $empty>&nbsp;</DataTableCell>
            </tr>
        );
    }

    return (
        <FlexContainer>
            { isFetching && (
                <FlexContainer
                    justifyContent="center"
                    alignItems="center"
                    color="#fff"
                    style={{
                        maxWidth: "98.625rem",
                        height: "44.188rem",
                        border: "1px solid #D8DEEC",
                        borderRadius: "16px",
                        background: "#FFFFFF",
                        boxShadow: "0 18px 36px rgba(29, 42, 89, 0.08)",
                    }}
                >
                    <PurpleLoadingIndicator />
                </FlexContainer>
            )}

            { !isFetching && (
                <>
                    <UpperControls>
                        <PortalTableControls>
                            <PortalSearchInput placeholder="Search listing" type="text" value={searchQuery} onChange={handleSearchChange}/>
                            <PortalSelect value={filterOption} onChange={ (e) => changeTableFilter(e.target.value, filterByExpiry) } width="15rem">
                                <option value={FILTER_DIRECTORY.EMPLOYEE}>{capitalizeString(pluralize.singular(customNames.employees))} Directory</option>
                                <option value={FILTER_DIRECTORY.CLIENT}>{capitalizeString(pluralize.singular(customNames.clients))} Directory</option>
                                <option value={FILTER_DIRECTORY.EMPLOYEE_AND_CLIENT}>{capitalizeString(pluralize.singular(customNames.employees))} & {capitalizeString(pluralize.singular(customNames.clients))}</option>
                            </PortalSelect>
                            <PortalSelect value={filterByExpiry} onChange={(e) => changeTableFilter(filterOption, e.target.value)} width="14rem">
                                <option value={FILTER_BY.ALL_CONTACTS}>All Listings</option>
                                <option value={FILTER_BY.EXPIRED}>Expired Listings</option>
                                <option value={FILTER_BY.NO_EXPIRY}>Listings with no Expiry</option>
                            </PortalSelect>
                            <UpperButtons
                                color="var(--calendar-text, #171A22)"
                                backgroundColor="#F3F5FB"
                                border="1px solid #D8DEEC"
                                padding="0 0.95rem"
                                onClick={handleShowSortModal}
                            >
                                Change Filter
                                <img
                                    src={FilterIcon}
                                    alt=""
                                    height="13.7px"
                                    width="14.5px"
                                    style={{ marginLeft: "0.55rem" }}
                                />
                            </UpperButtons>
                            {checkedContacts.length > 0 && (
                                <UpperButtons
                                    color="#FF3B3A"
                                    backgroundColor="#FFF5F5"
                                    padding="0.625rem 0.938rem 0.625rem"
                                    border="1px solid #FF3B3A"
                                    onClick={showDeleteDialog}
                                >
                                    Delete Listing
                                </UpperButtons>
                            )}
                        </PortalTableControls>
                        <PortalTableActions>
                            <ActivateFeatureButton width="150px" onClick={showAddContactForm}>
                                Add New Listing
                            </ActivateFeatureButton>
                        </PortalTableActions>
                    </UpperControls>
                    <DataTableContainer>
                        <DataTable minWidth="1120px" rowHeight="3.05rem" cellPadding="0.45rem 0.85rem" headerPadding="0.7rem 0.85rem">
                            <thead>
                                <tr>{headings}</tr>
                            </thead>
                            <tbody>{contactsList}</tbody>
                        </DataTable>
                    </DataTableContainer>
                </>
            )}
        </FlexContainer>
    );
};

function PurpleLoadingIndicator() {
    return (
        <FlexContainer>
            <LottieAnimation animationJsonData={PurpleLottieJson} />
        </FlexContainer>
    );
}

export default DirectoryTable;
